import { graphql } from '../../gql';
import { useModalContext } from '../../modal/ModalContext';
import { ModalActions } from '../../modal/components/ModalActions';
import { ModalContent } from '../../modal/components/ModalContent';
import { useApiMutation } from '../../query/hooks';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const deleteMetricDataPointMutation = graphql(`
	mutation connections_deleteMetricDataPoint($contentMetricDataPointId: UUID!) {
		deleteContentMetricDataPoint(by: { id: $contentMetricDataPointId }) {
			ok
			errorMessage
		}
	}
`);

const deleteDataPointModalSchema = z.object({
	contentMetricDataPointId: z.string(),
});

const DeleteDataPointModal: FC<z.infer<typeof deleteDataPointModalSchema>> = ({ contentMetricDataPointId }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { closeModal } = useModalContext();

	const { mutate: deleteMetricDataPoint } = useApiMutation(deleteMetricDataPointMutation, {
		onSuccess: (result) =>
			mutationResponseHandler({
				result: result.deleteContentMetricDataPoint.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'connections.metric-data-points.success.delete' }),
					error: intl.formatMessage({ id: 'connections.metric-data-points.error.delete' }),
				},
				errorMessage: result.deleteContentMetricDataPoint.errorMessage,
				openSnackbar,
			}),
	});

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'connections.metric-data-points.delete-modal.title' })}
				description={intl.formatMessage({ id: 'connections.metric-data-points.delete-modal.description' })}
			/>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteMetricDataPoint({ contentMetricDataPointId });
					closeModal();
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'common.delete' })}
			/>
		</>
	);
};

export { deleteDataPointModalSchema, DeleteDataPointModal };
